type NexwiseCartResultItem = {
  id?: string;
  quantity?: number | string;
  pricesingle?: number | string;
  productType?: string;
};

type NexwiseCartResultPayload = {
  event?: string;
  nexwise_pw_cart_status?: string;
  nexwise_pw_details?: Array<NexwiseCartResultItem & { items?: NexwiseCartResultItem[] }>;
};

type NexwiseMessageItem = {
  itemid?: string;
  quantity?: number | string;
  productType?: string;
};

export type NexwiseResolvedItem = {
  id: string;
  quantity: number;
  pricesingle: number;
  productType: string;
};

type InitNexwiseProductWizardBridgeOptions = {
  onResolvedItem: (item: NexwiseResolvedItem) => void;
  fallbackDelayMs?: number;
};

const toNumber = (value: unknown, fallback = 0): number => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const getDataLayer = (): unknown[] => {
  const win = window as Window & { dataLayer?: unknown[] };
  win.dataLayer = win.dataLayer || [];
  return win.dataLayer;
};

const getItemsFromPayload = (payload: NexwiseCartResultPayload): NexwiseCartResultItem[] => {
  if (!Array.isArray(payload?.nexwise_pw_details)) {
    return [];
  }

  return payload.nexwise_pw_details.flatMap((detail) => {
    if (Array.isArray(detail?.items)) {
      return detail.items;
    }

    return detail && typeof detail === 'object' ? [detail] : [];
  });
};

const getLatestDataLayerItem = (itemId?: string): NexwiseCartResultItem | null => {
  const dataLayer = getDataLayer() as NexwiseCartResultPayload[];

  const latestCartResult = [...dataLayer]
    .reverse()
    .find(
      (entry) =>
        entry?.event === 'nexwise_pw:cart_result' && entry?.nexwise_pw_cart_status === 'success',
    );

  if (!latestCartResult) {
    return null;
  }

  const allItems = getItemsFromPayload(latestCartResult);

  if (!allItems.length) {
    return null;
  }

  if (itemId) {
    const matched = allItems.find((cartItem) => String(cartItem?.id) === String(itemId));
    if (matched) {
      return matched;
    }
  }

  return allItems[0] || null;
};

const mapResolvedItem = (item: NexwiseCartResultItem): NexwiseResolvedItem => {
  return {
    id: String(item.id || ''),
    quantity: toNumber(item.quantity, 1),
    pricesingle: toNumber(item.pricesingle, 0),
    productType: String(item.productType || ''),
  };
};

export const initNexwiseProductWizardBridge = ({
  onResolvedItem,
  fallbackDelayMs = 4000,
}: InitNexwiseProductWizardBridgeOptions) => {
  let pendingMessageItem: NexwiseMessageItem | null = null;
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

  const clearFallback = () => {
    if (fallbackTimer) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  };

  const resolveFromDataLayerAndDispatch = (itemId?: string): boolean => {
    const latestItem = getLatestDataLayerItem(itemId);
    if (!latestItem) {
      return false;
    }

    onResolvedItem(mapResolvedItem(latestItem));
    return true;
  };

  const handleNexwiseCartResultEntry = (entry: unknown) => {
    if (!pendingMessageItem) {
      return;
    }

    const payload = entry as NexwiseCartResultPayload;

    if (payload?.event !== 'nexwise_pw:cart_result') {
      return;
    }

    if (payload?.nexwise_pw_cart_status !== 'success') {
      return;
    }

    const allItems = getItemsFromPayload(payload);

    if (!allItems.length) {
      return;
    }

    const matched = allItems.find(
      (item) => String(item?.id) === String(pendingMessageItem?.itemid),
    );
    const resolvedItem = matched || allItems[0];

    clearFallback();
    pendingMessageItem = null;

    onResolvedItem(mapResolvedItem(resolvedItem));
  };

  const dataLayer = getDataLayer() as unknown[] & {
    push: (...entries: unknown[]) => number;
  };
  const originalPush = dataLayer.push.bind(dataLayer);

  dataLayer.push = (...entries: unknown[]) => {
    entries.forEach((entry) => handleNexwiseCartResultEntry(entry));
    return originalPush(...entries);
  };

  const handleMessage = (event: MessageEvent) => {
    if (event.data?.type !== 'NEXWISE_ADD_TO_CART' || !event.data?.items?.length) {
      return;
    }

    const item = event.data.items[0] as NexwiseMessageItem;
    pendingMessageItem = item;

    if (resolveFromDataLayerAndDispatch(item.itemid)) {
      pendingMessageItem = null;
      clearFallback();
      return;
    }

    clearFallback();
    fallbackTimer = setTimeout(() => {
      if (!pendingMessageItem) {
        return;
      }

      if (resolveFromDataLayerAndDispatch(pendingMessageItem.itemid)) {
        pendingMessageItem = null;
        fallbackTimer = null;
        return;
      }

      onResolvedItem({
        id: String(pendingMessageItem.itemid || ''),
        quantity: toNumber(pendingMessageItem.quantity, 1),
        pricesingle: 0,
        productType: String(pendingMessageItem.productType || ''),
      });

      pendingMessageItem = null;
      fallbackTimer = null;
    }, fallbackDelayMs);
  };

  window.addEventListener('message', handleMessage, false);

  return () => {
    window.removeEventListener('message', handleMessage, false);

    clearFallback();
    pendingMessageItem = null;
    dataLayer.push = originalPush;
  };
};
